// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as React from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

interface Props<Values extends FieldValues> extends UseControllerProps<Values> {
  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * A react-hook-form control object used to connect the input element to the
   * form.
   */
  control: Control<Values>;

  /**
   * If true, the input will take up the full width of its container.
   */
  fullWidth?: boolean;

  /**
   * If true, the input will be a multiline text field.
   *
   * @default false
   */
  isMultiline?: boolean;

  /**
   * The minimum number of rows to display when the input is multiline.
   * If multiline is false, this prop is ignored.
   *
   * @default 1
   */
  minimumRows?: number;

  /**
   * The system prop that allows defining system overrides as well as additional
   * CSS styles.
   */
  sx?: TextFieldProps['sx'];

  /**
   * The placeholder text to display when the input is empty.
   */
  placeholder?: string;

  /**
   * Is the field required
   */
  required?: boolean;

  /**
   * Should this be auto-focused on open?
   */
  autoFocus?: boolean;
  /**
   * Field type to use - defaults to text
   */
  type?: React.InputHTMLAttributes<unknown>['type'];
  /**
   * If true, the input is disabled.
   */
  disabled?: boolean;
}

/**
 * A controlled text field component that integrates with react-hook-form.
 */
export function FormTextField<Values extends FieldValues>(
  props: Props<Values>
) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  // This component is always controlled, so an undefined or null value must
  // be coallesced to an empty string.
  const value = field.value ?? '';

  return (
    <TextField
      sx={props.sx}
      size="small"
      multiline={props.isMultiline}
      minRows={props.isMultiline ? props.minimumRows : undefined}
      name={field.name}
      value={value}
      label={props.label}
      error={Boolean(fieldState.error)}
      onBlur={field.onBlur}
      variant="outlined"
      inputRef={field.ref}
      onChange={field.onChange}
      fullWidth={props.fullWidth}
      helperText={fieldState.error?.message}
      placeholder={props.placeholder}
      required={props.required}
      autoFocus={props.autoFocus}
      type={props.type}
      disabled={props.disabled}
    />
  );
}
